import React, { createContext, useState, useContext, useEffect } from 'react';
import {
  signup as signupApi,
  login as loginApi,
  googleLogin as googleLoginApi,
  verifyToken,
  verifyLoginOtp as verifyLoginOtpApi,
  loginMobile as loginMobileApi,
  addPhone as addPhoneApi,
  verifyPhone as verifyPhoneApi,
} from '../services/authApi';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [requiresPhone, setRequiresPhone] = useState(false);

  // Check for existing token on mount
  useEffect(() => {
    const initAuth = async () => {
      try {
        const storedToken = localStorage.getItem('yovo_auth_token');
        if (storedToken) {
          const data = await verifyToken(storedToken);
          if (data.success && data.user) {
            setToken(storedToken);
            setUser(data.user);
            if (data.requiresPhone) setRequiresPhone(true);
          } else {
            localStorage.removeItem('yovo_auth_token');
          }
        }
      } catch (error) {
        // Token invalid or expired
        localStorage.removeItem('yovo_auth_token');
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, []);

  const signup = async (userData) => {
    try {
      const data = await signupApi(userData);
      if (data.success && data.token && data.user) {
        localStorage.setItem('yovo_auth_token', data.token);
        setToken(data.token);
        setUser(data.user);
        return { success: true, user: data.user, claimedBookings: data.claimedBookings || 0 };
      }
      throw new Error('Signup failed');
    } catch (error) {
      throw error;
    }
  };

  const login = async (username, password) => {
    try {
      const data = await loginApi(username, password);
      if (data.success && data.token && data.user) {
        localStorage.setItem('yovo_auth_token', data.token);
        setToken(data.token);
        setUser(data.user);
        if (data.requiresPhone) setRequiresPhone(true);
        return { success: true, user: data.user, claimedBookings: data.claimedBookings || 0, requiresPhone: data.requiresPhone };
      }
      throw new Error('Login failed');
    } catch (error) {
      throw error;
    }
  };

  const googleLogin = async (credential) => {
    try {
      const data = await googleLoginApi(credential);
      if (data.success && data.token && data.user) {
        localStorage.setItem('yovo_auth_token', data.token);
        setToken(data.token);
        setUser(data.user);
        if (data.requiresPhone) setRequiresPhone(true);
        return { success: true, user: data.user, claimedBookings: data.claimedBookings || 0, requiresPhone: data.requiresPhone };
      }
      throw new Error('Google login failed');
    } catch (error) {
      throw error;
    }
  };

  const mobileOtpLogin = async (phone, otp) => {
    const data = await verifyLoginOtpApi(phone, otp);
    if (data.success && data.needsSignup) {
      return { success: true, needsSignup: true, verifiedPhone: data.verifiedPhone };
    }
    if (data.success && data.token && data.user) {
      localStorage.setItem('yovo_auth_token', data.token);
      setToken(data.token);
      setUser(data.user);
      return { success: true, user: data.user, claimedBookings: data.claimedBookings || 0 };
    }
    throw new Error('OTP login failed');
  };

  const mobilePasswordLogin = async (phone, password) => {
    const data = await loginMobileApi(phone, password);
    if (data.success && data.token && data.user) {
      localStorage.setItem('yovo_auth_token', data.token);
      setToken(data.token);
      setUser(data.user);
      return { success: true, user: data.user, claimedBookings: data.claimedBookings || 0 };
    }
    throw new Error('Mobile login failed');
  };

  const linkPhone = async (phone, otp) => {
    const storedToken = token || localStorage.getItem('yovo_auth_token');
    const data = await verifyPhoneApi(phone, otp, storedToken);
    if (data.success) {
      setUser(prev => ({ ...prev, phone_number: data.phone_number }));
      setRequiresPhone(false);
      localStorage.removeItem('phone_prompt_dismissed');
    }
    return data;
  };

  const logout = () => {
    localStorage.removeItem('yovo_auth_token');
    setToken(null);
    setUser(null);
  };

  const value = {
    user,
    token,
    isAuthenticated: !!user && !!token,
    loading,
    requiresPhone,
    setRequiresPhone,
    signup,
    login,
    googleLogin,
    mobileOtpLogin,
    mobilePasswordLogin,
    linkPhone,
    logout
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

